import { useSendCommandAsync } from '@/_core/cqrs/use';
import { RealTimeAction } from '@/_core/fluxstore/types';
import { createCreateNewPersonaCommand } from '../../../api/commands/factory/createPersonaCommands';
import { PersonaCustomField, PersonaPersonality, StorymapState } from '../../types';
import { useStorymapStore } from '../../useModule';
import cloneDeep from 'lodash/cloneDeep';

const { registerReducer, registerMiddleware } = useStorymapStore();

export class CreateNewPersona extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'CreateNewPersona';
    public customFieldsToSave: PersonaCustomField[] = [];
    public personalitiesToSave: PersonaPersonality[] = [];

    public constructor(
        public id: string,
        public name: string,
        public group: string = null,
        public description: string = null,
        public pictureId: string = null,
        public customFields: { id: string; label: string; value: string }[] = [],
        public personalities: { id: string; startingLabel: string; endingLabel: string; value: number }[] = []
    ) {
        super(CreateNewPersona.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: CreateNewPersona): Promise<boolean> => {
    action.customFieldsToSave = resolveCustomFieldsToSave(state, action);
    action.personalitiesToSave = resolvePersonalitiesToSave(state, action);

    return await useSendCommandAsync(action, {
        createCommand: async () => await createCreateNewPersonaCommand(action),
    });
};

const reducer = (state: StorymapState, action: CreateNewPersona): void => {
    let persona = state.personas.find((x) => x.id === action.id);
    const defaultPersonaPicture = state.personaPictures.find((x) => x.id == '0');

    //on realtime sync event there's no empty persona
    if (persona == null) {
        state.personas.push({
            id: action.id,
            isNew: true,
            group: null,
            description: null,
            name: null,
            isGlobal: false,
            picture: defaultPersonaPicture.path,
            customFields: cloneDeep(state.defaultPersonaCustomFields),
            personalities: cloneDeep(state.defaultPersonaPersonalities),
        });
        persona = state.personas.find((x) => x.id === action.id);
    }

    persona.name = action.name;
    persona.group = action.group;
    persona.description = action.description;
    persona.picture = state.personaPictures.find((x) => x.id === action.pictureId)?.path ?? defaultPersonaPicture.path;

    const customFieldsToApply = resolveCustomFieldsToSave(state, action);
    if (customFieldsToApply.length > 0) {
        customFieldsToApply.forEach((x) => {
            const customfield = persona.customFields.find((y) => y.id == x.id);
            if (customfield != null) {
                customfield.value = x.value;
            }
        });
    }

    const personalitiesToApply = resolvePersonalitiesToSave(state, action);
    if (personalitiesToApply.length > 0) {
        personalitiesToApply.forEach((x) => {
            const personality = persona.personalities.find((y) => y.id == x.id);
            if (personality != null) {
                personality.value = x.value;
            }
        });
    }

    persona.isNew = false;
};

function resolveCustomFieldsToSave(state: StorymapState, action: CreateNewPersona): PersonaCustomField[] {
    if (action.customFieldsToSave != null && action.customFieldsToSave.length > 0) {
        return action.customFieldsToSave;
    }

    if (action.customFields == null || action.customFields.length === 0) {
        return [];
    }

    return action.customFields
        .map((x) => {
            const customField = state.defaultPersonaCustomFields.find((y) => y.id === x.id);
            if (customField != null) {
                return { id: customField.id, label: customField.label, value: x.value };
            }

            return x.id ? { id: x.id, label: x.label, value: x.value } : null;
        })
        .filter((x): x is PersonaCustomField => x != null);
}

function resolvePersonalitiesToSave(state: StorymapState, action: CreateNewPersona): PersonaPersonality[] {
    if (action.personalitiesToSave != null && action.personalitiesToSave.length > 0) {
        return action.personalitiesToSave;
    }

    if (action.personalities == null || action.personalities.length === 0) {
        return [];
    }

    return action.personalities
        .map((x) => {
            const personality = state.defaultPersonaPersonalities.find((y) => y.id === x.id);
            if (personality != null) {
                return {
                    id: personality.id,
                    startingLabel: personality.startingLabel,
                    endingLabel: personality.endingLabel,
                    value: x.value,
                };
            }

            return x.id
                ? {
                      id: x.id,
                      startingLabel: x.startingLabel,
                      endingLabel: x.endingLabel,
                      value: x.value,
                  }
                : null;
        })
        .filter((x): x is PersonaPersonality => x != null);
}

//@ts-ignore
registerMiddleware(CreateNewPersona.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(CreateNewPersona.ACTION_TYPE, reducer);
